import { ChangeDetectionStrategy, Component, inject, OnDestroy, OnInit, signal } from '@angular/core';
import { NGXLogger } from 'ngx-logger';
import { interval, Subscription, timer } from 'rxjs';
import { Feature } from '../../model/features.enum';
import { AuthService } from '../../services/auth.service';
import { FeatureToggleService } from '../../services/feature-toggle/feature-toggle.service';
import { OauthService } from '../../services/oauth/oauth.service';
import { WebConfigService } from '../../services/web-config/web-config.service';

@Component({
  selector: 'app-timer-display',
  imports: [],
  template: ` <span [hidden]="hidden()" id="timeout" [textContent]="timeLeft()"></span> `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TimerDisplayComponent implements OnInit, OnDestroy {
  #authService = inject(AuthService);
  #webConfig = inject(WebConfigService);
  #featureToggle = inject(FeatureToggleService);
  #oauthService = inject(OauthService);
  #isProd = false;
  timer: Subscription;
  #logger = inject(NGXLogger);
  timeLeft = signal<string>('Token: 0:00 minutes left');
  hidden = signal<boolean>(false);
  minutes: number = 0;
  seconds: number = 0;

  ngOnInit(): void {
    this.#isProd = this.#webConfig.env === 'prod';
    this.hidden.set(this.#isProd);
    this.#featureToggle
      .when(Feature.WebBackend)
      .isOn(() => this.getBackendTimeLeft())
      .isOff(() => {
        this.timer = interval(1000).subscribe(() => this.getHaloTimeLeft());
      })();
  }

  ngOnDestroy(): void {
    this.timer?.unsubscribe();
  }

  getBackendTimeLeft(callCount = 0): void {
    if (callCount > 3) {
      return;
    }
    this.#oauthService.expiryTime().subscribe((expiry) => {
      if (expiry < Date.now()) {
        return;
      }
      timer(0, 1000).subscribe(() => this.#getTimeLeft(expiry, 1));
      const remaining: number = this.#calculateRemainingTime(expiry, 1);
      if (remaining <= 1000) {
        this.getBackendTimeLeft(++callCount);
      } else {
        timer(remaining - 1000).subscribe(() => this.getBackendTimeLeft(++callCount));
      }
    });
  }

  getHaloTimeLeft(): void {
    try {
      const accessToken = this.#authService.accessToken;
      const expiry = JSON.parse(atob(accessToken.split('.')[1])).exp;
      const remaining: number = this.#calculateRemainingTime(expiry);
      this.#getTimeLeft(expiry);
      if (remaining <= 1000) {
        this.#authService.tryRefresh();
      }
    } catch (error) {
      this.#logger.error('Error calculating time left:', error);
      this.timeLeft.set('Token: 0:00 minutes left');
    }
  }

  #calculateRemainingTime(expiry: number, multiplier = 1000) {
    const current: number = +new Date();
    const expires: number = expiry * multiplier;
    const remaining: number = expires - current;
    return remaining;
  }

  #getTimeLeft(expiry: number, multiplier = 1000) {
    try {
      const decimal: number = 100;
      const remaining: number = this.#calculateRemainingTime(expiry, multiplier);
      const time: number = Math.round((remaining / 1000 / 60) * decimal) / decimal;
      this.minutes = Math.floor(time);
      this.seconds = Math.floor(((time - this.minutes) * 100 * 60) / 100);
      this.timeLeft.set(`Token: ${this.minutes}:${this.seconds} minutes left`);
    } catch (error) {
      this.#logger.error('Error calculating time left:', error);
      this.timeLeft.set('Token: 0:00 minutes left');
    }
  }
}
